import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';

import Layout from '../UI/introGameLayout/layout';
import QuestionsTransactionsTable from '../UI/questionsTransactionsTable';

import {
  generalInfoSummaryQuery,
  consolidatedInsightsQuery,
} from './api';

import {
  popupMessagesSummaryDecisionBreakdown,
  summaryDecisionBreakdownElementsHeaders,
  summaryDecisionBreakdownElementsToDiplay
} from './data';

import PopupMessages from '../UI/introGameLayout/popupMessages';

import './styles.css';

export default function BalancedEquationSummaryDecisionBreakdown() {
  const navigate = useNavigate();

  const {
    getUser,
    getCurrentBalancedEquationGameId
  } = useAuth();

  const user = getUser();

  const gameId = getCurrentBalancedEquationGameId();

  const [elementsTableData, setElementsTableData] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const [showDecisionBreakdownSecondPage, setShowDecisionBreakdownSecondPage] = useState(false);
  const [isFirstGameOfTheType, setIsFirstGameOfTheType] = useState(false);

  const { data: generalInfo } = useQuery(generalInfoSummaryQuery()) || {};
  const { data: consolidatedInsights } = useQuery({
    ...consolidatedInsightsQuery({ userId: user?.id }),
    enabled: !!user?.id,
  });

  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  useEffect(() => {
    if(gamesSessions && games && gameId && user) {
      if(gamesSessions.filter(
          session =>
            games.find(game => game.id === session.game && game.game_type === 'Balanced Equation')
        ).length < 2) {
        setIsFirstGameOfTheType(true);
      }
    }
  }, [gamesSessions, games, gameId, user]);

  useEffect(() => {
    if (consolidatedInsights) {
      const equationInsights = consolidatedInsights.balanced_equation || [];

      // Check if any entry has design 3 (account-level decisions with selected_change)
      const hasDesignThree = equationInsights.some(insight =>
        (insight.item_options || []).some(io =>
          (io.selected_change_options || []).some(sco => sco.selected_change !== null)
        )
      );
      setShowDecisionBreakdownSecondPage(hasDesignThree);

      const elementsData = [];

      equationInsights.forEach(insight => {
        const row = {
          question: insight.question,
        };

        let hasDesignOneOrTwo = false;

        (insight.item_options || []).forEach(itemOption => {
          const headerName = itemOption.item_option;

          (itemOption.selected_change_options || []).forEach(sco => {
            // Design 1: Increase/Decrease (selected_change is null)
            if (sco.selected_change === null && (sco.selected_option === 'Increase' || sco.selected_option === 'Decrease')) {
              hasDesignOneOrTwo = true;
              const isCorrect = sco.is_correct;
              const value = sco.selected_option;

              if (!summaryDecisionBreakdownElementsToDiplay.includes(headerName)) {
                if (row[headerName]) {
                  row[headerName].push({
                    value,
                    isCorrect,
                    background: isCorrect ? '#D1E9D6' : '#FBD8D8',
                  });
                } else {
                  row[headerName] = [{
                    value,
                    background: isCorrect ? '#D1E9D6' : '#FBD8D8',
                  }];
                }
              }
            }

            // Design 2: Debit/Credit (selected_change is null)
            if (sco.selected_change === null && (sco.selected_option === 'Debit' || sco.selected_option === 'Credit')) {
              hasDesignOneOrTwo = true;
              const isCorrect = sco.is_correct;
              const value = sco.selected_option;

              if (!summaryDecisionBreakdownElementsToDiplay.includes(headerName)) {
                if (row[headerName]) {
                  row[headerName].push({
                    value,
                    isCorrect,
                    background: isCorrect ? '#D1E9D6' : '#FBD8D8',
                  });
                } else {
                  row[headerName] = [{
                    value,
                    background: isCorrect ? '#D1E9D6' : '#FBD8D8',
                  }];
                }
              }
            }
          });
        });

        if (hasDesignOneOrTwo) {
          elementsData.push(row);
        }
      });

      setElementsTableData(elementsData);
      setDataLoaded(true);
    }
  }, [consolidatedInsights]);

  const handlePopupMessagesNextButtonClick = () => {
    let redirectUrl = '';
    if(showDecisionBreakdownSecondPage) {
      redirectUrl = '/balanced-equation/summary/decision-breakdown-accounts';
    } else {
      redirectUrl = '/balanced-equation/summary/final';
      if(isFirstGameOfTheType) {
        redirectUrl = '/balanced-equation/summary/feedback';
      }
    }
    navigate(redirectUrl);
  }

  const handlePopupMessagesPrevButtonClick = () => {
    navigate('/balanced-equation/summary/breakdown');
  }

  return(
    <Layout
      backgroundColor="#FBEDD5"
      isBackgroundBlurred={false}
      isHeaderVisible={true}
      isFooterVisible={false}
    >
      {
        dataLoaded &&
          <div className="game-summary-container game-summary-decision-questions-transactions-container">
            <PopupMessages
              messages={popupMessagesSummaryDecisionBreakdown}
              messageSize="xx-small"
              buttonText="Next"
              onButtonClick={handlePopupMessagesNextButtonClick}
              additionalButtonText="Back"
              onAdditionalButtonClick={handlePopupMessagesPrevButtonClick}
              isAvatarOnTop={true}
            />
            <QuestionsTransactionsTable
              tableColumnHeader="Transactions"
              tableHeaders={summaryDecisionBreakdownElementsHeaders}
              tableData={elementsTableData}
            />
          </div>
      }
    </Layout>
  );
}
